import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Send, Mic, Loader2, CheckCircle, AlertCircle, Globe } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

interface LocationSummary {
  text: string;
  audioUrl?: string;
  facts: string[];
  weather?: string;
}

export default function LocationPulse() {
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState<LocationData | null>(null);
  const [summary, setSummary] = useState<LocationSummary | null>(null);
  const [status, setStatus] = useState<'idle' | 'fetching' | 'processing' | 'sending' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [language, setLanguage] = useState<'en' | 'pt'>('en');
  const [shareGlobally, setShareGlobally] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState('📍');
  
  // Detect Safari, iOS, language, and check if coming from global map
  useEffect(() => {
    // Check if user came from global map or has 'share' param
    const urlParams = new URLSearchParams(window.location.search);
    const fromGlobal = document.referrer.includes('/global') || urlParams.get('share') === 'true';
    if (fromGlobal) {
      setShareGlobally(true);
    }
    const ua = navigator.userAgent;
    const safari = /^((?!chrome|android).)*safari/i.test(ua);
    const ios = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    setIsSafari(safari);
    setIsIOS(ios);
    
    // Detect language (Brazilian Portuguese or English)
    const browserLang = navigator.language || (navigator as any).userLanguage;
    if (browserLang.startsWith('pt')) {
      setLanguage('pt');
    }
  }, []);

  // Translations
  const t = {
    en: {
      title: 'What To Do Nearby',
      subtitle: 'Get personalized activity suggestions for your location',
      button: {
        idle: 'Get Activity Suggestions',
        fetching: 'Finding your location...',
        processing: 'Finding things to do...',
        sending: 'Sending recommendations...',
        success: 'Sent successfully!',
        error: 'Something went wrong'
      },
      safari: {
        title: '📱 Safari on iPhone/iPad:',
        before: 'Before clicking the button above, please ensure:',
        step1: 'Settings → Privacy & Security → Location Services',
        step1_action: 'is ON',
        step2: 'Location Services → Safari Websites',
        step2_action: 'is set to "While Using App"',
        step3: 'When prompted, tap',
        step3_action: '"Allow"',
        step3_end: 'to share location',
        tip: 'Safari may show the prompt at the top of the screen'
      },
      error: {
        title: '⚠️ Location Access Required',
        denied: 'Location permission denied. Please enable location access in your browser settings.',
        unavailable: 'Location unavailable. Please check your device\'s location settings.',
        timeout: 'Location request timed out. Please try again.',
        notSupported: 'Location services not available. Please use a modern browser.',
        safariDenied: 'Location access denied. Please check Safari settings.',
        safariTitle: 'For Safari on iPhone/iPad:',
        step1: 'Open Settings app',
        step2: 'Go to Privacy & Security → Location Services',
        step3: 'Make sure Location Services is ON',
        step4: 'Scroll down to Safari Websites',
        step5: 'Set to "While Using App" or "Ask"',
        step6: 'Return here and try again',
        tip: 'Tip: You may need to close and reopen Safari after changing settings'
      }
    },
    pt: {
      title: 'O Que Fazer Por Perto',
      subtitle: 'Receba sugestões personalizadas de atividades para sua localização',
      button: {
        idle: 'Ver Sugestões de Atividades',
        fetching: 'Encontrando sua localização...',
        processing: 'Buscando coisas para fazer...',
        sending: 'Enviando recomendações...',
        success: 'Enviado com sucesso!',
        error: 'Algo deu errado'
      },
      safari: {
        title: '📱 Safari no iPhone/iPad:',
        before: 'Antes de tocar no botão acima, por favor verifique:',
        step1: 'Ajustes → Privacidade e Segurança → Serviços de Localização',
        step1_action: 'está ATIVADO',
        step2: 'Serviços de Localização → Sites do Safari',
        step2_action: 'está configurado como "Ao Usar o App"',
        step3: 'Quando aparecer a pergunta, toque em',
        step3_action: '"Permitir"',
        step3_end: 'para compartilhar localização',
        tip: 'O Safari pode mostrar a pergunta no topo da tela'
      },
      error: {
        title: '⚠️ Acesso à Localização Necessário',
        denied: 'Permissão de localização negada. Por favor, ative o acesso à localização nas configurações.',
        unavailable: 'Localização indisponível. Verifique as configurações de localização do seu dispositivo.',
        timeout: 'Tempo esgotado. Por favor, tente novamente.',
        notSupported: 'Serviços de localização não disponíveis. Use um navegador moderno.',
        safariDenied: 'Acesso à localização negado. Verifique as configurações do Safari.',
        safariTitle: 'Para Safari no iPhone/iPad:',
        step1: 'Abra o app Ajustes (ícone de engrenagem)',
        step2: 'Vá em Privacidade e Segurança → Serviços de Localização',
        step3: 'Certifique-se que Serviços de Localização está ATIVADO (verde)',
        step4: 'Role para baixo até encontrar Sites do Safari',
        step5: 'Toque e selecione "Ao Usar o App" ou "Perguntar"',
        step6: 'Volte aqui e tente novamente',
        tip: 'Dica: Você pode precisar fechar e abrir o Safari novamente após mudar as configurações'
      }
    }
  };

  const texts = t[language];
  
  // Feature labels
  const features = {
    en: {
      gps: 'GPS Location',
      ai: 'AI Recommendations',
      telegram: 'Telegram'
    },
    pt: {
      gps: 'Localização GPS',
      ai: 'Recomendações de IA',
      telegram: 'Telegram'
    }
  };

  const handleLocationPulse = async () => {
    setIsLoading(true);
    setError(null);
    setStatus('fetching');

    try {
      // Check if geolocation is available
      if (!navigator.geolocation) {
        throw new Error(texts.error.notSupported);
      }

      // For iOS Safari, check permissions if available
      if (isIOS && navigator.permissions) {
        try {
          const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          if (permission.state === 'denied') {
            throw new Error(texts.error.safariDenied);
          }
        } catch (permError) {
          // Permissions API might not be fully supported, continue anyway
          console.log('Permissions check not available');
        }
      }

      // Step 1: Get user's location
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000, // Increased for mobile/international users
          maximumAge: 0
        });
      });

      const locationData: LocationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: Date.now()
      };

      setLocation(locationData);
      setStatus('processing');

      // Step 2: Try to get AI recommendations
      try {
        // First get geocoding data - request in English for consistency
        const geocodeResponse = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${locationData.latitude}&lon=${locationData.longitude}&format=json&accept-language=en`,
          { headers: { 'User-Agent': 'LocationPulse/1.0' } }
        );
        const geocodeData = await geocodeResponse.json();
        
        // Log geocode data for debugging
        console.log('Geocode data:', {
          city: geocodeData.address?.city || geocodeData.address?.town,
          country: geocodeData.address?.country,
          display_name: geocodeData.display_name
        });

        // Get AI recommendations
        const now = new Date();
        const hour = now.getHours();
        const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' });
        const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

        const aiResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/location/ai-summary', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: locationData,
            geocode: geocodeData,
            prompt: `Location: ${geocodeData.display_name || 'Unknown'}
Time: ${dayOfWeek} ${timeOfDay}

List exactly 3 activities to do RIGHT NOW in this area:

1. [First activity - include specific place name and address if possible]
2. [Second activity - include specific place name and address if possible]  
3. [Third activity - include specific place name and address if possible]

Keep each activity to 1-2 sentences. Be specific and actionable.`
          })
        });

        const aiData = await aiResponse.json();
        
        const summaryData = {
          text: aiData.summary?.text || aiData.summary?.raw || `You're near ${geocodeData.display_name || 'your location'}`,
          facts: aiData.summary?.facts || [
            `📍 ${geocodeData.address?.city || geocodeData.address?.town || 'Your area'}`,
            `🌍 ${geocodeData.address?.country || 'Earth'}`,
            `⏰ ${new Date().toLocaleTimeString()}`
          ]
        };
        
        setSummary(summaryData);
        
        // Share globally if opted in
        if (shareGlobally) {
          try {
            // Extract activities from AI response - could be in text or facts
            const activities = [];
            
            // Try to parse numbered list from main text
            if (aiData.summary?.text || aiData.summary?.raw) {
              const text = aiData.summary?.text || aiData.summary?.raw || '';
              const lines = text.split('\n');
              for (const line of lines) {
                if (line.match(/^[1-3]\./)) {
                  activities.push(line.replace(/^[1-3]\.\s*/, '').trim());
                }
              }
            }
            
            // If no activities found in text, use facts
            if (activities.length === 0 && aiData.summary?.facts) {
              activities.push(...aiData.summary.facts);
            }
            
            // Only share if we have activities
            if (activities.length > 0) {
              const globalShareResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/global/share', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  location: locationData,
                  activities: activities,
                  city: geocodeData.address?.city || geocodeData.address?.town,
                  country: geocodeData.address?.country,
                  place_name: geocodeData.display_name,
                  time_of_day: timeOfDay,
                  language: language,
                  emoji: selectedEmoji,
                  color: '#3B82F6'
                })
              });
              
              const globalResult = await globalShareResponse.json();
              
              // If it's a duplicate, show a user-friendly message
              if (globalResult.isDuplicate) {
                console.log(`Duplicate detected: Wait ${globalResult.minutesRemaining} more minutes before sharing from this location again`);
                // We could store this in state and show it to the user if needed
                // For now, we'll just silently skip sharing to avoid spam
              }
            }
          } catch (err) {
            console.error('Failed to share globally:', err);
            // Log for debugging (activities variable is in parent scope)
            console.error('Share failed for location:', {
              city: geocodeData.address?.city || geocodeData.address?.town,
              country: geocodeData.address?.country
            });
          }
        }
        
        setStatus('sending');

        // Step 3: Send to Telegram with AI data
        const telegramResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/location/telegram/location-pulse', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: locationData,
            geocode: geocodeData,
            summary: summaryData
          })
        });
        
        if (!telegramResponse.ok) throw new Error('Failed to send to Telegram');
      } catch (err) {
        console.error('AI processing error:', err);
        // Fallback to basic summary
        const fallbackSummary = {
          text: `You're at coordinates ${locationData.latitude.toFixed(6)}, ${locationData.longitude.toFixed(6)}`,
          facts: [
            `📍 Latitude: ${locationData.latitude.toFixed(6)}`,
            `📍 Longitude: ${locationData.longitude.toFixed(6)}`,
            `🎯 Accuracy: ±${Math.round(locationData.accuracy)}m`
          ]
        };
        setSummary(fallbackSummary);
        setStatus('sending');

        // Send basic data to Telegram
        const telegramResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/location/telegram/location-pulse', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: locationData,
            summary: fallbackSummary
          })
        });
        
        if (!telegramResponse.ok) throw new Error('Failed to send to Telegram');
      }

      setStatus('success');
    } catch (err) {
      // Provide more helpful error messages
      let errorMessage = 'Something went wrong';
      
      if (err instanceof GeolocationPositionError) {
        switch(err.code) {
          case 1: // PERMISSION_DENIED
            errorMessage = texts.error.denied;
            break;
          case 2: // POSITION_UNAVAILABLE
            errorMessage = texts.error.unavailable;
            break;
          case 3: // TIMEOUT
            errorMessage = texts.error.timeout;
            break;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'fetching': return texts.button.fetching;
      case 'processing': return texts.button.processing;
      case 'sending': return texts.button.sending;
      case 'success': return texts.button.success;
      case 'error': return error || texts.button.error;
      default: return texts.button.idle;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md md:max-w-2xl lg:max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Language Toggle */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setLanguage(language === 'en' ? 'pt' : 'en')}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100"
            >
              {language === 'en' ? '🇧🇷 Português' : '🇺🇸 English'}
            </button>
          </div>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{texts.title}</h1>
            <p className="text-gray-600">{texts.subtitle}</p>
            <Link 
              to="/global"
              className="inline-flex items-center gap-2 mt-4 text-sm text-indigo-600 hover:text-indigo-700 transition-colors"
            >
              <Globe className="w-4 h-4" />
              View Global Activity Map
            </Link>
          </div>

          {/* Share Globally Option */}
          {(!location || status === 'idle') && (
            <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareGlobally}
                  onChange={(e) => setShareGlobally(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-800">
                    Share anonymously with the world 🌍
                  </span>
                  <p className="text-xs text-gray-600 mt-1">
                    Help others discover activities by sharing your finds (no personal data)
                  </p>
                </div>
              </label>
              {shareGlobally && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-gray-600">Pick your emoji:</span>
                  {['📍', '🎯', '🚀', '💫', '🌟', '🎨', '🎭', '🍕'].map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => setSelectedEmoji(emoji)}
                      className={`text-xl p-1 rounded ${selectedEmoji === emoji ? 'bg-indigo-100' : ''}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Main Button - Hide when showing results */}
          {(!location || status === 'idle' || status === 'error') && (
            <button
              onClick={handleLocationPulse}
              disabled={isLoading || status === 'success'}
              className={`w-full py-4 px-6 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-3 ${
              isLoading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : status === 'success'
                ? 'bg-green-500 text-white cursor-default'
                : status === 'error'
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 transform hover:scale-105'
            }`}
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : status === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : status === 'error' ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <Send className="w-5 h-5" />
            )}
              <span>{getStatusMessage()}</span>
            </button>
          )}

          {/* Status Progress - Show as loading state */}
          {status !== 'idle' && status !== 'error' && status !== 'success' && (
            <div className="mt-6 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
              <p className="text-sm md:text-base text-gray-600 animate-pulse">{getStatusMessage()}</p>
            </div>
          )}

          {/* Debug Toggle for Technical Info */}
          {location && (
            <>
              <button
                onClick={() => setShowDebug(!showDebug)}
                className="mt-4 text-xs text-gray-500 hover:text-gray-700 underline"
              >
                {showDebug ? (language === 'pt' ? 'Ocultar' : 'Hide') : (language === 'pt' ? 'Mostrar' : 'Show')} {language === 'pt' ? 'detalhes técnicos' : 'technical details'}
              </button>
              
              {showDebug && (
                <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Current Location</span>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Lat: {location.latitude.toFixed(6)}</p>
                    <p>Lng: {location.longitude.toFixed(6)}</p>
                    <p>Accuracy: ±{Math.round(location.accuracy)}m</p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Summary Display */}
          {summary && (
            <div className="mt-6">
              <div className="p-4 md:p-6 lg:p-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <Mic className="w-5 h-5 md:w-6 md:h-6 text-purple-500" />
                  <span className="text-sm md:text-base lg:text-lg font-bold text-gray-800">
                    {language === 'pt' ? 'Recomendações de IA' : 'AI Recommendations'}
                  </span>
                </div>
                <div className="text-sm md:text-base lg:text-lg text-gray-700 leading-relaxed">
                  <ReactMarkdown 
                    className="prose prose-sm md:prose-base max-w-none prose-headings:text-gray-800 prose-strong:text-gray-900 prose-ul:list-disc prose-ul:ml-4"
                    components={{
                      p: ({children}) => <p className="mb-3">{children}</p>,
                      ul: ({children}) => <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>,
                      ol: ({children}) => <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>,
                      li: ({children}) => <li className="text-gray-700">{children}</li>,
                      strong: ({children}) => <strong className="font-bold text-gray-900">{children}</strong>,
                    }}
                  >
                    {summary.text}
                  </ReactMarkdown>
                </div>
                {summary.facts && summary.facts.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-100">
                    <div className="text-xs md:text-sm font-semibold text-gray-600 mb-2">
                      {language === 'pt' ? 'Dicas Rápidas:' : 'Quick Tips:'}
                    </div>
                    <div className="space-y-2">
                      {summary.facts.map((fact, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="text-purple-500 text-sm md:text-base">💡</span>
                          <span className="text-xs md:text-sm text-gray-600">{fact}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Features - Only show before action */}
          {!location && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl mb-1">📍</div>
                  <p className="text-xs text-gray-600">{features[language].gps}</p>
                </div>
                <div>
                  <div className="text-2xl mb-1">🤖</div>
                  <p className="text-xs text-gray-600">{features[language].ai}</p>
                </div>
                <div>
                  <div className="text-2xl mb-1">✈️</div>
                  <p className="text-xs text-gray-600">{features[language].telegram}</p>
                </div>
              </div>
            </div>
          )}

          {/* Safari/iOS specific warning */}
          {isIOS && status === 'idle' && (
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{texts.safari.title}</h3>
              <p className="text-xs text-gray-600 mb-2">{texts.safari.before}</p>
              <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                <li><strong>{texts.safari.step1}</strong> {texts.safari.step1_action}</li>
                <li><strong>{texts.safari.step2}</strong> {texts.safari.step2_action}</li>
                <li>{texts.safari.step3} <strong>{texts.safari.step3_action}</strong> {texts.safari.step3_end}</li>
              </ol>
              <p className="text-xs text-gray-500 mt-2 italic">{texts.safari.tip}</p>
            </div>
          )}

          {/* Help for mobile users */}
          {status === 'error' && (
            <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{texts.error.title}</h3>
              
              {isIOS ? (
                <>
                  <p className="text-xs text-gray-600 font-semibold mb-2">{texts.error.safariTitle}</p>
                  <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside">
                    <li>{texts.error.step1}</li>
                    <li>{texts.error.step2}</li>
                    <li>{texts.error.step3}</li>
                    <li>{texts.error.step4}</li>
                    <li>{texts.error.step5}</li>
                    <li>{texts.error.step6}</li>
                  </ol>
                  <div className="mt-3 p-2 bg-blue-50 rounded">
                    <p className="text-xs text-blue-700">
                      💡 {texts.error.tip}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-600 font-semibold mb-2">To enable location:</p>
                  <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                    <li>Tap the button above to start</li>
                    <li>When prompted, tap "Allow" to share location</li>
                    <li>If blocked, go to browser settings → Site settings → Location</li>
                    <li>For Android: Browser menu → Settings → Site settings → Location</li>
                  </ol>
                </>
              )}
            </div>
          )}
          
          {/* Success Actions */}
          {status === 'success' && (
            <div className="mt-6 text-center space-y-3">
              {shareGlobally && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700 font-medium mb-2">
                    🎉 {language === 'pt' ? 'Compartilhado globalmente!' : 'Shared globally!'}
                  </p>
                  <a
                    href="/global"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all"
                  >
                    🌍 {language === 'pt' ? 'Ver Mapa Global' : 'View Global Map'}
                  </a>
                </div>
              )}
              <button
                onClick={() => {
                  setStatus('idle');
                  setLocation(null);
                  setSummary(null);
                  setError(null);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 underline"
              >
                {language === 'pt' ? 'Tentar novamente' : 'Try again'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}